/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash, Check, X, Download, Lock, LockOpen, FileText } from '@phosphor-icons/react';
import { projectsApi } from '../lib/api';
import { formatDate } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'deplacement', label: 'Deplacement' },
  { value: 'hebergement', label: 'Hebergement' },
  { value: 'repas', label: 'Repas' },
  { value: 'fournitures', label: 'Fournitures' },
  { value: 'transport', label: 'Transport' },
  { value: 'autre', label: 'Autre' },
];

const statusLabels = { pending: 'En attente', approved: 'Approuvee', rejected: 'Refusee' };
const statusColors = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-600' };

export default function ExpensesPage() {
  const { user, isAdmin } = useAuth();
  const isManager = ['admin', 'supervisor', 'manager', 'project_manager'].includes(user?.role);
  const [expenses, setExpenses] = useState([]);
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], description: '', amount: '', category: 'deplacement', project_id: '', billable: false });

  const fetchData = async () => {
    try {
      const [expRes, projRes, repRes] = await Promise.all([
        fetch(`${API}/api/expenses`, { credentials: 'include' }).then(r => r.json()),
        projectsApi.getAll().then(r => r.data),
        fetch(`${API}/api/expenses/reports`, { credentials: 'include' }).then(r => r.json()).catch(() => []),
      ]);
      setExpenses(expRes);
      setProjects(projRes);
      setReports(Array.isArray(repRes) ? repRes : []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    if (!form.description || !form.amount) return;
    try {
      const fd = new FormData();
      fd.append('date', form.date);
      fd.append('description', form.description);
      fd.append('amount', form.amount);
      fd.append('category', form.category);
      if (form.project_id) fd.append('project_id', form.project_id);
      fd.append('billable', form.billable);
      if (form.receipt) fd.append('receipt', form.receipt);

      const res = await fetch(`${API}/api/expenses`, { method: 'POST', body: fd, credentials: 'include' });
      if (!res.ok) throw new Error();
      toast.success('Note de frais soumise');
      setDialog(false);
      setForm({ date: new Date().toISOString().split('T')[0], description: '', amount: '', category: 'deplacement', project_id: '', billable: false });
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleValidate = async (id, action) => {
    try {
      await fetch(`${API}/api/expenses/${id}/validate`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ action }),
      });
      toast.success(action === 'approve' ? 'Approuvee' : 'Refusee');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    await fetch(`${API}/api/expenses/${id}`, { method: 'DELETE', credentials: 'include' });
    toast.success('Supprimee'); fetchData();
  };

  const totalPending = expenses.filter(e => e.status === 'pending').reduce((s, e) => s + (e.amount || 0), 0);
  const totalApproved = expenses.filter(e => e.status === 'approved').reduce((s, e) => s + (e.amount || 0), 0);

  // Group user's own expenses by month for the "Rapports mensuels" tab
  const groupByMonth = () => {
    const map = {};
    expenses.filter(e => e.user_id === user?.id).forEach(e => {
      const m = (e.date || '').slice(0, 7);
      if (!m) return;
      if (!map[m]) map[m] = { month: m, items: [], total: 0, pending: 0, approved: 0, rejected: 0 };
      map[m].items.push(e);
      map[m].total += e.amount || 0;
      const st = e.status || 'pending';
      if (st === 'approved') map[m].approved += e.amount || 0;
      else if (st === 'rejected') map[m].rejected += e.amount || 0;
      else map[m].pending += e.amount || 0;
    });
    return Object.values(map).sort((a, b) => b.month.localeCompare(a.month));
  };
  const monthlyGroups = groupByMonth();
  const reportsByMonth = reports.reduce((acc, r) => { acc[r.month] = r; return acc; }, {});

  const handleSubmitMonth = async (month) => {
    if (!window.confirm(`Soumettre la note de frais mensuelle pour ${month} ? Les notes seront verrouillées.`)) return;
    try {
      const res = await fetch(`${API}/api/expenses/reports/submit`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(data.message); fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const handleReportAction = async (reportId, action, reason) => {
    try {
      const res = await fetch(`${API}/api/expenses/reports/${reportId}/action`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, reason }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Rapport ${action === 'approve' ? 'approuvé' : action === 'reject' ? 'rejeté' : 'déverrouillé'}`);
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const downloadReportPdf = async (uid, month) => {
    try {
      const res = await fetch(`${API}/api/expenses/monthly/pdf?user_id=${uid}&month=${month}`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `note_de_frais_${month}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch { toast.error('Erreur PDF'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="p-5 space-y-4" data-testid="expenses-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-heading font-extrabold text-slate-900">Notes de frais</h1>
          <p className="text-xs text-slate-500">{expenses.length} note(s) | En attente: {totalPending.toFixed(2)} EUR | Approuvees: {totalApproved.toFixed(2)} EUR</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={async () => {
            const m = window.prompt('Mois à exporter (format YYYY-MM)', new Date().toISOString().slice(0, 7));
            if (!m) return;
            try {
              const res = await fetch(`${API}/api/expenses/monthly/pdf?user_id=${user.id}&month=${m}`, { credentials: 'include' });
              if (!res.ok) throw new Error();
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url;
              a.download = `note_de_frais_${m}.pdf`;
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
              toast.success('PDF généré');
            } catch { toast.error('Erreur PDF'); }
          }} data-testid="generate-monthly-pdf-btn">
            <Download size={14} className="mr-1" />Note mensuelle PDF
          </Button>
          <Button className="btn-primary" size="sm" onClick={() => setDialog(true)} data-testid="new-expense-btn">
            <Plus size={14} className="mr-1" />Nouvelle note
          </Button>
        </div>
      </div>

      <Tabs defaultValue="list" className="space-y-3">
        <TabsList className="bg-white border">
          <TabsTrigger value="list" className="text-xs gap-1.5" data-testid="tab-list">Toutes les notes</TabsTrigger>
          <TabsTrigger value="monthly" className="text-xs gap-1.5" data-testid="tab-monthly">Rapports mensuels{monthlyGroups.length > 0 ? ` (${monthlyGroups.length})` : ''}</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
      <Card className="card-surface">
        <CardContent className="p-0">
          {expenses.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Aucune note de frais</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-slate-50">
                <th className="px-4 py-2.5 text-left text-xs font-semibold">Date</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold">Collaborateur</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold">Description</th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold">Categorie</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold">Montant</th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold">Statut</th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold">Justificatif</th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold w-28">Actions</th>
              </tr></thead>
              <tbody>
                {expenses.map(e => (
                  <tr key={e.id} className="border-b hover:bg-slate-50" data-testid={`expense-${e.id}`}>
                    <td className="px-4 py-2 text-xs">{e.date ? formatDate(e.date) : '-'}</td>
                    <td className="px-4 py-2 text-xs font-medium">{e.user_name || '-'}</td>
                    <td className="px-4 py-2 text-xs">{e.description}</td>
                    <td className="px-4 py-2 text-center"><Badge className="text-[10px] bg-slate-100">{categories.find(c => c.value === e.category)?.label || e.category}</Badge></td>
                    <td className="px-4 py-2 text-right font-bold">{(e.amount || 0).toFixed(2)} EUR</td>
                    <td className="px-4 py-2 text-center"><Badge className={`text-[10px] ${statusColors[e.status] || ''}`}>{statusLabels[e.status] || e.status}</Badge></td>
                    <td className="px-4 py-2 text-center">
                      {e.receipt_url ? <a href={`${API}${e.receipt_url}`} target="_blank" rel="noreferrer" className="text-blue-600 text-xs hover:underline">Voir</a> : <span className="text-xs text-slate-400">-</span>}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {isManager && e.status === 'pending' && (
                          <>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => handleValidate(e.id, 'approve')} title="Approuver"><Check size={14} weight="bold" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleValidate(e.id, 'reject')} title="Refuser"><X size={14} weight="bold" /></Button>
                          </>
                        )}
                        {(e.status === 'pending' || e.status === 'rejected') && (e.user_id === user?.id || isAdmin) && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(e.id)}><Trash size={12} /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      </TabsContent>

      <TabsContent value="monthly">
        <div className="space-y-2">
          {monthlyGroups.length === 0 ? (
            <Card className="card-surface"><CardContent className="py-8 text-center text-sm text-slate-500">Vous n'avez aucune note de frais</CardContent></Card>
          ) : monthlyGroups.map(g => {
            const report = reportsByMonth[g.month];
            const locked = report?.locked;
            const reportStatus = report?.status;
            const monthLabel = new Date(g.month + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
            return (
              <Card key={g.month} className="card-surface" data-testid={`month-card-${g.month}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-slate-800 capitalize">{monthLabel}</h3>
                        {locked && <Badge className="text-[10px] bg-slate-200 text-slate-700"><Lock size={10} className="mr-0.5" />Verrouillé</Badge>}
                        {reportStatus === 'submitted' && <Badge className="text-[10px] bg-blue-100 text-blue-700">Soumis - en attente de validation</Badge>}
                        {reportStatus === 'approved' && <Badge className="text-[10px] bg-green-100 text-green-700">Approuvé</Badge>}
                        {reportStatus === 'rejected' && <Badge className="text-[10px] bg-red-100 text-red-600">Rejeté</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {g.items.length} note(s) • Total <b>{g.total.toFixed(2)} €</b>
                        {g.pending > 0 && <span className="text-amber-600 ml-2">{g.pending.toFixed(2)}€ en attente</span>}
                        {g.approved > 0 && <span className="text-green-600 ml-2">{g.approved.toFixed(2)}€ approuvés</span>}
                        {g.rejected > 0 && <span className="text-red-500 ml-2">{g.rejected.toFixed(2)}€ rejetés</span>}
                      </p>
                      {report?.reject_reason && <p className="text-[11px] text-red-600 mt-1 italic">Motif: {report.reject_reason}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => downloadReportPdf(user.id, g.month)} data-testid={`pdf-${g.month}`}>
                        <FileText size={12} className="mr-1" />PDF
                      </Button>
                      {!locked && reportStatus !== 'approved' && (
                        <Button className="btn-primary h-8 text-xs" size="sm" onClick={() => handleSubmitMonth(g.month)} data-testid={`submit-${g.month}`}>
                          <Lock size={12} className="mr-1" />Soumettre & verrouiller
                        </Button>
                      )}
                      {isManager && report && reportStatus === 'submitted' && (
                        <>
                          <Button variant="outline" size="sm" className="h-8 text-xs text-green-700 border-green-300" onClick={() => handleReportAction(report.id, 'approve')} data-testid={`approve-${g.month}`}>
                            <Check size={12} className="mr-1" />Approuver
                          </Button>
                          <Button variant="outline" size="sm" className="h-8 text-xs text-red-700 border-red-300" onClick={() => { const r = window.prompt('Motif du rejet ?'); if (r !== null) handleReportAction(report.id, 'reject', r); }} data-testid={`reject-${g.month}`}>
                            <X size={12} className="mr-1" />Rejeter
                          </Button>
                        </>
                      )}
                      {isManager && locked && (
                        <Button variant="outline" size="sm" className="h-8 text-xs text-amber-700 border-amber-300" onClick={() => handleReportAction(report.id, 'unlock')} data-testid={`unlock-${g.month}`}>
                          <LockOpen size={12} className="mr-1" />Déverrouiller
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </TabsContent>
      </Tabs>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nouvelle note de frais</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Date *</Label><Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} /></div>
              <div className="space-y-1"><Label className="text-xs">Montant (EUR) *</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} placeholder="150.00" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Description *</Label><Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Deplacement client Paris..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Categorie</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Projet (optionnel)</Label>
                <Select value={form.project_id || 'none'} onValueChange={v => setForm(p => ({ ...p, project_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Justificatif (PDF, image)</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setForm(p => ({ ...p, receipt: e.target.files?.[0] || null }))} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="billable-exp" checked={form.billable} onChange={e => setForm(p => ({ ...p, billable: e.target.checked }))} />
              <Label htmlFor="billable-exp" className="text-xs cursor-pointer">Refacturable au client</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setDialog(false)}>Annuler</Button>
              <Button className="btn-primary" size="sm" onClick={handleCreate} disabled={!form.description || !form.amount}>Soumettre</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
